/**
 * Alta y edición de un usuario de administración.
 *
 * El alta crea (o reutiliza, si el correo ya existe en el realm) la identidad
 * de Keycloak, le concede el rol de backoffice y le abre ficha local. La
 * edición cambia datos y rol. El rol NO se puede tocar cuando el acceso viene
 * de un rol de realm (`managedHere: false`): ese realm lo comparten el resto
 * de productos Roommatik y quitárselo aquí podría dejarle sin acceso a ellos.
 */

import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import {
  createPlatformUser,
  updatePlatformUser,
} from '../services/platformUserService';
import {
  PLATFORM_ROLE_ID,
  type PlatformRoleName,
  type PlatformUserFormValues,
  type PlatformUserRecord,
} from '../types/platformUser';

const i18nBase = 'resources.platform-users';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Misma política que el resto del admin (la del realm de Keycloak). */
const PASSWORD_PATTERN =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{12,}$/;

export interface PlatformUserFormDialogProps {
  /** null = alta. */
  record: PlatformUserRecord | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyDefaults: PlatformUserFormValues = {
  email: '',
  name: '',
  surname: '',
  roleName: 'Maintenance',
  password: '',
};

export const PlatformUserFormDialog: React.FC<PlatformUserFormDialogProps> = ({
  record,
  open,
  onClose,
  onSuccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isEdit = record !== null;
  const roleLocked = isEdit && !record.managedHere;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<PlatformUserFormValues>({
    defaultValues: emptyDefaults,
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!open) return;
    setShowPassword(false);
    reset({
      email: record?.email ?? '',
      name: record?.name ?? '',
      surname: record?.surname ?? '',
      roleName: record?.roleName ?? 'Maintenance',
      password: '',
    });
  }, [open, record, reset]);

  const onSubmit = async (values: PlatformUserFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      if (isEdit) {
        await updatePlatformUser(record.id, {
          email: values.email.trim(),
          name: values.name.trim(),
          surname: values.surname.trim(),
          // Un rol bloqueado no se manda: el backend lo rechazaría.
          role: roleLocked ? undefined : PLATFORM_ROLE_ID[values.roleName],
        });
        notify(`${i18nBase}.notifications.updated`, { type: 'success' });
      } else {
        await createPlatformUser({
          email: values.email.trim(),
          name: values.name.trim() || undefined,
          surname: values.surname.trim() || undefined,
          role: PLATFORM_ROLE_ID[values.roleName],
          password: values.password || undefined,
        });
        notify(`${i18nBase}.notifications.created`, { type: 'success' });
      }
      onSuccess();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, 'ra.notification.http_error', translate),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const roleOptions: PlatformRoleName[] = ['SuperUser', 'Maintenance'];

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(`${i18nBase}.dialog.${isEdit ? 'edit' : 'create'}`)}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            {roleLocked && (
              <Alert severity="info" sx={{ fontSize: 12.5 }}>
                {translate(`${i18nBase}.help.realmRoleLocked`)}
              </Alert>
            )}

            <Controller
              name="email"
              control={control}
              rules={{
                required: translate(`${i18nBase}.validation.emailRequired`),
                pattern: {
                  value: EMAIL_PATTERN,
                  message: translate(`${i18nBase}.validation.emailInvalid`),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  type="email"
                  label={translate(`${i18nBase}.fields.email`)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate(`${i18nBase}.fields.name`)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate(`${i18nBase}.fields.surname`)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Box>

            <Controller
              name="roleName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  disabled={roleLocked}
                  label={translate(`${i18nBase}.fields.role`)}
                  InputLabelProps={{ shrink: true }}
                  helperText={translate(
                    `${i18nBase}.help.role${field.value === 'SuperUser' ? 'SuperUser' : 'Maintenance'}`
                  )}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role}>
                      {translate(`${i18nBase}.roles.${role}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {!isEdit && (
              <Controller
                name="password"
                control={control}
                rules={{
                  validate: (value) =>
                    !value ||
                    PASSWORD_PATTERN.test(value) ||
                    translate(`${i18nBase}.validation.passwordPattern`),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    label={translate(`${i18nBase}.fields.password`)}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      translate(`${i18nBase}.help.passwordOptional`)
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((v) => !v)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                height: 40,
              }}
            >
              {translate('ra.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !isValid}
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                height: 40,
              }}
            >
              {translate('ra.action.save')}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PlatformUserFormDialog;
